import { Component } from '../lib/ComponentBase';
import { registerComponent } from '../lib/ComponentHelpers';
import type { IntentionProvider, ProposalProvider, AttackPositionProvider, MoveProvider, DisplayProvider, DisplayInfo, PathfindingNode, WorldState } from '../types/Components';
import type { Entity } from '../types/Entity';
import type { Intention } from '../types/AI';
import type { Proposal } from '../lib/Proposal';
import type HexCoords from '../grid/HexCoords';
import HexCoordsClass from '../grid/HexCoords';
import ActorStats from './ActorStats';
import HexPosition from './HexPosition';
import { ActionType, ActionMode } from '../lib/Action';
import { ProposalType } from '../lib/Proposal';
import Action from '../lib/Action';
import HexOffsetSet from '../grid/HexOffsetSet';
import HexOffset from '../grid/HexOffset';

/**
 * A generic ability for ranged attacks based on a HexOffsetSet pattern.
 * This is intended to be subclassed by concrete abilities that define a rangePattern.
 */
class RangedAttack extends Component implements IntentionProvider, ProposalProvider, AttackPositionProvider, MoveProvider, DisplayProvider {
  static supportedModes = [ActionMode.RANGED_ATTACK];

  damage: number;
  priority: number;
  rangePattern: HexOffsetSet;

  constructor(data: { damage: number, priority: number, rangePattern: HexOffsetSet | { offsets: { q: number, r: number }[] } }) {
    super();
    this.damage = data.damage;
    this.priority = data.priority;
    // Hydrate rangePattern if it's raw JSON data
    this.rangePattern = data.rangePattern instanceof HexOffsetSet
      ? data.rangePattern
      : new HexOffsetSet(data.rangePattern.offsets);
  }

  equals(other: this): boolean {
    if (this.damage !== other.damage) return false;
    if (this.priority !== other.priority) return false;

    // Validate that rangePattern is properly hydrated as HexOffsetSet instance
    if (!(other.rangePattern instanceof HexOffsetSet)) {
      console.error(`RangedAttack.equals: other.rangePattern is not a HexOffsetSet instance`, other.rangePattern);
      return false;
    }

    // Validate that offsets are properly hydrated as HexOffset instances
    for (let i = 0; i < other.rangePattern.offsets.length; i++) {
      const offset = other.rangePattern.offsets[i];
      if (!(offset instanceof HexOffset)) {
        console.error(`RangedAttack.equals: offset[${i}] is not a HexOffset instance`, offset);
        return false;
      }
    }

    // Compare rangePattern offsets using JSON
    const thisOffsets = JSON.stringify(this.rangePattern.offsets.map(o => ({ q: o.q, r: o.r })));
    const otherOffsets = JSON.stringify(other.rangePattern.offsets.map(o => ({ q: o.q, r: o.r })));
    return thisOffsets === otherOffsets;
  }

  toJSON() {
    return {
      damage: this.damage,
      priority: this.priority,
      rangePattern: {
        offsets: this.rangePattern.offsets.map(o => ({ q: o.q, r: o.r })),
      },
    };
  }

  static getTestInstances(): RangedAttack[] {
    return [
      new RangedAttack({ damage: 100, priority: 30, rangePattern: HexOffsetSet.construct(HexOffsetSet.arc(2)) }),
      new RangedAttack({ damage: 50, priority: 25, rangePattern: HexOffsetSet.construct(HexOffsetSet.arc(3)) }),
    ];
  }

  getDisplayInfo(): DisplayInfo {
    return { icon: 'game-icons:crosshair', label: 'Ranged Attack', description: 'Attacks at range.' };
  }

  getIntention(entity: Entity, worldState: WorldState): Intention | null {
    const playerPos = worldState.player?.getComponent(HexPosition);
    const selfPos = entity.getComponent(HexPosition);
    if (!playerPos || !selfPos) return null;

    if (this.isValidTarget(selfPos, playerPos.coords)) {
      return {
        priority: this.priority,
        type: ActionType.RANGED_ATTACK,
        details: {
          coords: playerPos.coords,
          target: worldState.player,
        },
        sourceComponentName: 'RangedAttack',
      };
    }
    return null;
  }

  getPossiblePlayerActions(entity: Entity, mode: typeof ActionMode[keyof typeof ActionMode], worldState: WorldState): Map<string, Action> {
    const actions = new Map<string, Action>();
    if (mode !== ActionMode.RANGED_ATTACK) return actions;

    const { allCoords, occupiedCoords } = worldState;
    const selfPosition = entity.getComponent(HexPosition);

    if (!selfPosition || !allCoords) return actions;

    // Generate all possible targets from the current position, considering all 6 bearings.
    // This gives us an omni-directional targeting pattern, suitable for abilities like ThrowRock.
    const potentialTargets = new Map();
    for (let i = 1; i <= 6; i++) {
      const targets = this.rangePattern.getTargets(selfPosition.coords, i as import('../grid/HexBearing').Bearing);
      for (const target of targets) {
        // Ensure the target is a valid hex on the map before adding.
        if (allCoords.has(target.key)) {
          potentialTargets.set(target.key, target);
        }
      }
    }

    for (const targetCoords of potentialTargets.values()) {
      const details: any = { coords: targetCoords };
      if (occupiedCoords) {
        const target = occupiedCoords.get(targetCoords.key);
        if (target && target.hasComponent(ActorStats)) {
          details.target = target;
        }
      }
      actions.set(
        targetCoords.key,
        new Action(entity, ActionType.RANGED_ATTACK, details)
      );
    }
    return actions;
  }

  getProposedOutcome(selfAction: Action): Proposal | null {
    if (selfAction.type !== ActionType.RANGED_ATTACK) {
      return null;
    }

    const selfPos = selfAction.entity.getComponent(HexPosition);
    if (!selfPos) return null;

    const { coords } = selfAction.details;
    if (!coords || !this.isValidTarget(selfPos, coords)) {
      return null;
    }
    const projectileAnimation = {
      type: 'PROJECTILE' as const,
      entityId: selfAction.entity.id,
      sourceCoords: selfPos.coords,
      targetCoords: coords,
    };
    const destHex = selfPos.coords.neighbor(selfPos.bearing)
    const attackAnimation = {
      type: 'ATTACK_JAB' as const,
      entityId: selfAction.entity.id,
      targetCoords: destHex,
    };
    //const animationPlan = [projectileAnimation, attackAnimation];
    const animationPlan = [projectileAnimation];

    return {
      type: ProposalType.REPLACEMENT,
      priority: this.priority,
      effects: [
        {
          type: 'ATTACK' as const,
          attackTiming: 'END' as const,
          attackHex: coords,
          damage: this.damage,
          animationPlan: [animationPlan],
        },
      ],
    };
  }

  getPossibleMoves(fromNode: PathfindingNode, worldState: { walkableCoords: Set<string> }): PathfindingNode[] {
    // RangedAttack doesn't provide movement capabilities
    return [];
  }

  isValidTarget(startPos: HexPosition, targetCoords: HexCoords): boolean {
    const sources = this.rangePattern.getSources(targetCoords);
    return sources.some(source => source.coords.equalTo(startPos.coords) && source.bearing === startPos.bearing);
  }

  getAttackPositions(entity: Entity, worldState: WorldState): PathfindingNode[] {
    const playerPos = worldState.player?.getComponent(HexPosition);
    if (!playerPos) return [];

    return this.rangePattern.getSources(playerPos.coords);
  }

  getAttackPattern(entity: Entity): HexCoords[] {
    const selfPos = entity.getComponent(HexPosition);
    if (!selfPos) return [];

    // Generate attack pattern for the entity's current bearing only
    return this.rangePattern.getTargets(selfPos.coords, selfPos.bearing);
  }
}

export default registerComponent(RangedAttack);
